import React, { useEffect, useState, useCallback } from 'react';
import './BookmarksPanel.css';

function timeAgo(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── Collection Picker Modal ────────────────────────────────────
function CollectionPicker({ bookmark, collections, onAddToCollection, onRemoveFromCollection, onClose, onCreateCollection }) {
  const [newName, setNewName] = useState('');

  const handleCreate = () => {
    if (!newName.trim()) return;
    const id = onCreateCollection(newName);
    if (id) {
      onAddToCollection(bookmark.id, id);
      setNewName('');
    }
  };

  return (
    <div className="col-picker-overlay" onClick={onClose}>
      <div className="col-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="col-picker-header">
          <span>📁 Save to Collection</span>
          <button className="bm-close-btn" onClick={onClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="col-picker-list">
          {collections.length === 0 && (
            <p className="col-picker-empty">No collections yet — create one below!</p>
          )}
          {collections.map((col) => {
            const inCol = (bookmark.collectionIds || []).includes(col.id);
            return (
              <button
                key={col.id}
                className={`col-picker-item${inCol ? ' in-col' : ''}`}
                onClick={() => inCol ? onRemoveFromCollection(bookmark.id, col.id) : onAddToCollection(bookmark.id, col.id)}
              >
                <span className="col-picker-icon">{inCol ? '✅' : '📁'}</span>
                <span className="col-picker-name">{col.name}</span>
                {inCol && <span className="col-picker-check">Added</span>}
              </button>
            );
          })}
        </div>

        <div className="col-picker-create">
          <input
            className="col-picker-input"
            placeholder="New collection name…"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') onClose(); }}
            autoFocus
            maxLength={40}
          />
          <button className="col-picker-create-btn" onClick={handleCreate} disabled={!newName.trim()}>
            + Create
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bookmark Card ─────────────────────────────────────────────
function BmCard({ bm, collections, onSelect, onRemove, onOpenPicker, onRemoveFromCollection, activeCollectionId }) {
  const collectionNames = (bm.collectionIds || [])
    .map((id) => collections.find((c) => c.id === id)?.name)
    .filter(Boolean);

  return (
    <div className="bm-card" onClick={() => onSelect(bm)} title={bm.prompt}>
      <img className="bm-card-thumb" src={bm.imageUrl} alt={bm.prompt} loading="lazy" />
      <div className="bm-card-info">
        <div className="bm-card-prompt">{bm.prompt || 'Untitled design'}</div>
        <div className="bm-card-time">{timeAgo(bm.savedAt)}</div>
        {/* Collection tags */}
        {collectionNames.length > 0 && (
          <div className="bm-card-tags">
            {collectionNames.map((name, i) => (
              <span key={i} className="bm-card-tag">📁 {name}</span>
            ))}
          </div>
        )}
        <div className="bm-card-actions-row">
          <button
            className="bm-card-use-btn"
            onClick={(e) => { e.stopPropagation(); onSelect(bm); }}
          >
            ↩ Load
          </button>
          <button
            className="bm-card-col-btn"
            onClick={(e) => { e.stopPropagation(); onOpenPicker(bm); }}
            title="Add to collection"
          >
            📁 {collectionNames.length > 0 ? `${collectionNames.length}` : 'Save'}
          </button>
          {activeCollectionId && (
            <button
              className="bm-card-remove-col-btn"
              onClick={(e) => { e.stopPropagation(); onRemoveFromCollection(bm.id, activeCollectionId); }}
              title="Remove from this collection"
            >
              ✕
            </button>
          )}
        </div>
      </div>
      <button
        className="bm-card-remove"
        onClick={(e) => { e.stopPropagation(); onRemove(bm.id); }}
        aria-label="Remove bookmark"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

// ── Main Panel ────────────────────────────────────────────────
function BookmarksPanel({
  bookmarks,
  onClose,
  onSelect,
  onRemove,
  onClearAll,
  collections,
  onAddToCollection,
  onRemoveFromCollection,
  onCreateCollection,
  onDeleteCollection,
  onRenameCollection,
}) {
  const [activeTab, setActiveTab] = useState('all'); // 'all' | collectionId (number)
  const [pickerBookmark, setPickerBookmark] = useState(null);
  const [newColName, setNewColName] = useState('');
  const [showNewColInput, setShowNewColInput] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (pickerBookmark) { setPickerBookmark(null); return; }
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, pickerBookmark]);

  // Filter bookmarks by active tab
  const visibleBookmarks = activeTab === 'all'
    ? bookmarks
    : bookmarks.filter((bm) => (bm.collectionIds || []).includes(activeTab));

  const handleCreateCollection = useCallback(() => {
    if (!newColName.trim()) return;
    onCreateCollection(newColName);
    setNewColName('');
    setShowNewColInput(false);
  }, [newColName, onCreateCollection]);

  const handleStartRename = (col) => {
    setRenamingId(col.id);
    setRenameValue(col.name);
  };

  const handleRename = () => {
    if (renamingId && renameValue.trim()) {
      onRenameCollection(renamingId, renameValue);
    }
    setRenamingId(null);
    setRenameValue('');
  };

  const handleDeleteCollection = (col) => {
    if (!window.confirm(`Delete collection "${col.name}"? Designs will stay in All Bookmarks.`)) return;
    onDeleteCollection(col.id);
    if (activeTab === col.id) setActiveTab('all');
  };

  return (
    <>
      {/* Overlay */}
      <div className="bookmarks-overlay" onClick={onClose} />

      {/* Drawer */}
      <div className="bookmarks-drawer" role="dialog" aria-label="My Bookmarks">
        {/* Header */}
        <div className="bookmarks-drawer-header">
          <h3>
            🔖 My Bookmarks
            {bookmarks.length > 0 && (
              <span className="bm-count">{bookmarks.length}</span>
            )}
          </h3>
          <div className="bm-header-actions">
            {bookmarks.length > 0 && (
              <button
                className="bm-clear-btn"
                onClick={() => { if (window.confirm('Clear all bookmarks?')) onClearAll(); }}
              >
                Clear all
              </button>
            )}
            <button className="bm-close-btn" onClick={onClose} aria-label="Close bookmarks">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── Tab Bar ─────────────────────────────────────────── */}
        <div className="bm-tabs-bar">
          <button
            className={`bm-tab${activeTab === 'all' ? ' active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All <span className="bm-tab-count">{bookmarks.length}</span>
          </button>
          {collections.map((col) => {
            const count = bookmarks.filter((b) => (b.collectionIds || []).includes(col.id)).length;
            return (
              <div key={col.id} className="bm-tab-wrapper">
                {renamingId === col.id ? (
                  <input
                    className="bm-tab-rename-input"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={handleRename}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') { setRenamingId(null); } }}
                    autoFocus
                    maxLength={30}
                  />
                ) : (
                  <button
                    className={`bm-tab${activeTab === col.id ? ' active' : ''}`}
                    onClick={() => setActiveTab(col.id)}
                    onDoubleClick={() => handleStartRename(col)}
                    title="Double-click to rename"
                  >
                    📁 {col.name} <span className="bm-tab-count">{count}</span>
                  </button>
                )}
                {activeTab === col.id && renamingId !== col.id && (
                  <div className="bm-tab-col-actions">
                    <button className="bm-tab-action-btn" onClick={() => handleStartRename(col)} title="Rename">✏️</button>
                    <button className="bm-tab-action-btn danger" onClick={() => handleDeleteCollection(col)} title="Delete collection">🗑️</button>
                  </div>
                )}
              </div>
            );
          })}
          {/* + New Collection */}
          {showNewColInput ? (
            <div className="bm-new-col-inline">
              <input
                className="bm-new-col-input"
                placeholder="Collection name…"
                value={newColName}
                maxLength={40}
                autoFocus
                onChange={(e) => setNewColName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateCollection();
                  if (e.key === 'Escape') { setShowNewColInput(false); setNewColName(''); }
                }}
              />
              <button className="bm-new-col-ok" onClick={handleCreateCollection}>✓</button>
            </div>
          ) : (
            <button className="bm-tab bm-tab-new" onClick={() => setShowNewColInput(true)} title="Create new collection">
              + New
            </button>
          )}
        </div>

        {/* ── Collection description ───────────────────────────── */}
        {activeTab !== 'all' && (
          <div className="bm-col-desc">
            <span>📁 {collections.find((c) => c.id === activeTab)?.name}</span>
            <span className="bm-col-desc-count">{visibleBookmarks.length} design{visibleBookmarks.length !== 1 ? 's' : ''}</span>
          </div>
        )}

        {/* ── List ─────────────────────────────────────────────── */}
        {visibleBookmarks.length === 0 ? (
          <div className="bm-empty">
            <div className="bm-empty-icon">{activeTab === 'all' ? '🔖' : '📁'}</div>
            {activeTab === 'all' ? (
              <>
                <p>No bookmarks yet.</p>
                <p>Click the 🔖 button on any design to save it here.</p>
              </>
            ) : (
              <>
                <p>This collection is empty.</p>
                <p>Click 📁 on any bookmark to add it here.</p>
              </>
            )}
          </div>
        ) : (
          <div className="bookmarks-list">
            {visibleBookmarks.map((bm) => (
              <BmCard
                key={bm.id}
                bm={bm}
                collections={collections}
                onSelect={(b) => { onSelect(b); onClose(); }}
                onRemove={onRemove}
                onOpenPicker={setPickerBookmark}
                onRemoveFromCollection={onRemoveFromCollection}
                activeCollectionId={activeTab !== 'all' ? activeTab : null}
              />
            ))}
          </div>
        )}
      </div>

      {/* Collection Picker Modal */}
      {pickerBookmark && (
        <CollectionPicker
          bookmark={pickerBookmark}
          collections={collections}
          onAddToCollection={onAddToCollection}
          onRemoveFromCollection={onRemoveFromCollection}
          onCreateCollection={onCreateCollection}
          onClose={() => setPickerBookmark(null)}
        />
      )}
    </>
  );
}

export default BookmarksPanel;
